/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Authentication page handling user login. Manages form validation,
credentials verification, and secure session establishment.
*/

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Loader2, AlertCircle, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

const loginSchema = z.object({
  username: z.string().min(1, { message: 'Please enter your username' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  
  // Get the redirect path from location state or default to home
  const from = location.state?.from?.pathname || "/";
  
  // This effect will handle redirection after successful authentication
  useEffect(() => {
    if (isAuthenticated && !redirecting) {
      // Clear error messages
      setLoginError(null);
      // Mark as redirecting to prevent flashing
      setRedirecting(true);
      // Small delay to allow for smooth transition
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 100);
    }
  }, [isAuthenticated, navigate, from, redirecting]);
  
  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });
  
  const handleLogin = async (values: z.infer<typeof loginSchema>) => {
    setIsSubmitting(true);
    setLoginError(null);
    try {
      await login(values.username, values.password);
      // The useEffect will handle redirection
    } catch (error: any) {
      setLoginError(error.message || "Login failed. Please check your credentials and try again.");
      setIsSubmitting(false);
    }
  };
  
  // If we're redirecting, show a clean loading state
  if (redirecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-muted/20">
        <div className="space-y-4 text-center">
          <h2 className="text-2xl font-semibold">Logging you in</h2>
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
        </div>
      </div>
    );
  }
  
  // If we're already authenticated, show a minimal loading state
  if (isAuthenticated && !loginError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Show a loading state while auth is initializing (but not if we have an error)
  if (authLoading && !loginError && !isSubmitting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-muted/40 p-4">
      <div className="max-w-md w-full">
        <div className="flex justify-center mb-8">
          <div className="flex items-center">
            <img 
              src="/lovable-uploads/7f9a3b72-1a60-4ef2-b566-286979847f68.png" 
              alt="Garage" 
              className="h-12 w-auto"
            />
          </div>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Welcome to Garage</CardTitle>
            <CardDescription>
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loginError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{loginError}</AlertDescription>
              </Alert>
            )}
            
            <Form {...loginForm}>
              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <FormField
                  control={loginForm.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter your username"
                          type="text"
                          autoComplete="username"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        <Info className="h-3 w-3 inline mr-1" />
                        Enter your exact username
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={loginForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="••••••••"
                          type="password"
                          autoComplete="current-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button
                  type="submit"
                  className="w-full mt-6"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Logging in...
                    </>
                  ) : (
                    'Log In'
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex flex-col items-start">
            <p className="text-sm text-muted-foreground">
              Having trouble? Make sure:
            </p>
            <ul className="text-sm text-muted-foreground list-disc pl-5 mt-1">
              <li>Your username is entered exactly</li>
              <li>Your password is correct</li>
              <li>You are connected to the internet</li>
            </ul>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
